import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Badge, Card, CardBody, Chip } from '@vnetwork/ui/primitives';
import { StatusPill } from '@vnetwork/ui/data';
import { formatDate } from '@/lib/format';
import type { ContractOverviewItem } from '@/api/contracts';
import { contractStatusTone, statusLabel } from '../contract-format';

/* ─── helpers ─────────────────────────────────────────────────────────── */

const muted = { color: 'var(--vn-fg-muted)', fontStyle: 'italic' } as const;

/** Row for one label/value fact inside the card. */
function FactRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--vn-space-1)' }}>
      <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>{label}</span>
      <span style={{ fontSize: 'var(--vn-font-size-sm)' }}>{children}</span>
    </div>
  );
}

/* ─── component ───────────────────────────────────────────────────────── */

interface ContractOverviewCardProps {
  contract: ContractOverviewItem;
}

/**
 * Kanban card for one contract in the Overview tab. Shows contract number
 * (linked), company/customer name, industry, expiry date, first 3 services
 * (+ overflow badge), SLA/NDA badges, and the assigned employee name.
 * Uses `Card flat` + `CardBody` with `--vn-*` tokens only; no raw hex.
 */
export function ContractOverviewCard({ contract }: ContractOverviewCardProps) {
  const {
    contract_id,
    contract_number_id,
    contract_status,
    company_name,
    customer_name,
    customer_id,
    customer_industry,
    contract_expiry_date,
    services = [],
    sla_enable,
    nda_enable,
    employee_name,
  } = contract;

  const displayName = company_name || customer_name;
  const shownServices = services.slice(0, 3);
  const overflowServices = services.length - shownServices.length;

  return (
    <Card flat style={{ borderRadius: 'var(--vn-radius-md)' }}>
      <CardBody style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
        {/* Header: contract number link + status pill */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 'var(--vn-space-2)' }}>
          <Link
            to={`/contracts/${contract_id}`}
            style={{
              fontSize: 'var(--vn-font-size-sm)',
              fontWeight: 'var(--vn-font-weight-semibold)',
              color: 'var(--vn-primary-text)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {contract_number_id || `#${contract_id}`}
          </Link>
          <StatusPill tone={contractStatusTone(contract_status)}>
            {statusLabel(contract_status)}
          </StatusPill>
        </div>

        {/* Customer / company */}
        <FactRow label="Customer">
          {displayName ? (
            customer_id ? (
              <Link
                to={`/customers/${customer_id}`}
                style={{ color: 'var(--vn-primary-text)', fontSize: 'var(--vn-font-size-sm)' }}
              >
                {displayName}
              </Link>
            ) : (
              <span>{displayName}</span>
            )
          ) : (
            <span style={muted}>—</span>
          )}
        </FactRow>

        {/* Industry */}
        <FactRow label="Industry">
          {customer_industry ? (
            <span>{customer_industry}</span>
          ) : (
            <span style={muted}>—</span>
          )}
        </FactRow>

        {/* Expiry date */}
        <FactRow label="Expiry">
          <span style={{ whiteSpace: 'nowrap' }}>{formatDate(contract_expiry_date)}</span>
        </FactRow>

        {/* Services */}
        {shownServices.length > 0 ? (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--vn-space-1)' }}>
            {shownServices.map((svc) => (
              <Chip key={svc.service_id} appearance="subtle" tone="neutral" size="sm">
                {svc.service_name}
              </Chip>
            ))}
            {overflowServices > 0 ? (
              <Badge tone="neutral" size="sm">+{overflowServices}</Badge>
            ) : null}
          </div>
        ) : null}

        {/* SLA / NDA badges + employee */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 'var(--vn-space-2)' }}>
          <div style={{ display: 'flex', gap: 'var(--vn-space-1)' }}>
            {sla_enable ? <Badge tone="success" size="sm">SLA</Badge> : null}
            {nda_enable ? <Badge tone="warning" size="sm">NDA</Badge> : null}
          </div>
          {employee_name ? (
            <span style={{ fontSize: 'var(--vn-font-size-xs)', color: 'var(--vn-fg-muted)' }}>
              {employee_name}
            </span>
          ) : null}
        </div>
      </CardBody>
    </Card>
  );
}
